import { useEffect, useState } from 'react'
import { Box, Button, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material'
import { FunctionsHttpError } from '@supabase/supabase-js'
import { PLATAFORMAS } from '../api/trafegoPago'
import { supabase } from '../lib/supabase'
import { useAuth } from '../state/AuthContext'
import { useFiltros } from '../state/FiltrosContext'
import { acaoDaSugestao, PLATAFORMAS_ALTERAVEIS } from '../utils/alteracoes'
import { AplicarAlteracao, type PedidoAlteracao } from './AplicarAlteracao'

type Acao = 'aumentar' | 'reduzir' | 'pausar' | 'revisar' | 'manter'
type Sugestao = { campanha: string; campanha_id?: string; plataforma: string; acao: Acao; prioridade: 'alta' | 'media' | 'baixa'; texto: string }
type Analise = {
  resumo: string
  sugestoes: Sugestao[]
  campanhas: number
  fonte: 'motor' | 'ia'
  modelo: string | null
  iaDisponivel: boolean
  descartadas: number
  motivoSemIa?: string
}

const ACAO: Record<Acao, { texto: string; cor: 'success' | 'warning' | 'error' | 'info' | 'default' }> = {
  aumentar: { texto: 'Aumentar verba', cor: 'success' },
  reduzir: { texto: 'Reduzir verba', cor: 'warning' },
  pausar: { texto: 'Pausar', cor: 'error' },
  revisar: { texto: 'Revisar', cor: 'info' },
  manter: { texto: 'Manter', cor: 'default' },
}
const PRIORIDADE = { alta: 'Prioridade alta', media: 'Prioridade média', baixa: 'Prioridade baixa' }

async function pedir(corpo: Record<string, unknown>): Promise<Analise> {
  const { data, error } = await supabase.functions.invoke('leitura-ia', { body: { analise: 'campanhas', ...corpo } })
  if (error) {
    const detalhe = error instanceof FunctionsHttpError ? await error.context.json().catch(() => null) : null
    throw new Error(detalhe?.erro ?? error.message)
  }
  return data as Analise
}

/**
 * Sugestões por campanha no tráfego pago. Na hora: os sinais do motor (regras fixas, sem
 * custo). No botão: a análise da IA, que prioriza e explica — cada sugestão conferida no
 * servidor (campanha existe, números estão nos dados). Nada muda sozinho: quem tem
 * campanhas.alterar vê "Aplicar" nas de pausar e de verba (Meta Ads e Google Ads) e confirma no diálogo.
 */
export function AnaliseCampanhas({ plataforma }: { plataforma?: string }) {
  const { intervalo, marcaId } = useFiltros()
  const [analise, setAnalise] = useState<Analise | null>(null)
  const [analisando, setAnalisando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [aplicar, setAplicar] = useState<{ pedido: PedidoAlteracao; motivo: string } | null>(null)
  const { pode } = useAuth()
  const podeAplicar = pode('campanhas.alterar')

  const corpo = { inicio: intervalo.inicio, fim: intervalo.fim, marca_id: marcaId || null, plataforma: plataforma || null }
  const chaveFiltros = JSON.stringify(corpo)

  useEffect(() => {
    if (!marcaId) return
    let cancelado = false
    setAnalise(null)
    setErro(null)
    pedir({ ...JSON.parse(chaveFiltros), ia: false })
      .then((a) => !cancelado && setAnalise(a))
      .catch((e: Error) => !cancelado && setErro(e.message))
    return () => {
      cancelado = true
    }
  }, [chaveFiltros, marcaId])

  const analisarComIa = async () => {
    setAnalisando(true)
    setErro(null)
    try {
      setAnalise(await pedir({ ...corpo, ia: true }))
    } catch (e) {
      setErro((e as Error).message)
    } finally {
      setAnalisando(false)
    }
  }

  const titulo = (
    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif', mr: 'auto' }}>
      Sugestões para as campanhas
    </Typography>
  )

  if (!marcaId) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        {titulo}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Escolha uma marca para ver as sugestões: cada marca mede conversão de um jeito, então as campanhas só se
          comparam dentro da mesma marca.
        </Typography>
      </Paper>
    )
  }

  const ia = analise?.fonte === 'ia'

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
        {titulo}
        {analise ? (
          <Chip
            size="small"
            variant="outlined"
            color={ia ? 'primary' : 'default'}
            label={ia ? 'Análise da IA · números conferidos' : 'Sinais automáticos'}
          />
        ) : null}
        {analise?.iaDisponivel && !ia ? (
          <Button size="small" variant="contained" disabled={analisando} onClick={analisarComIa} sx={{ textTransform: 'none' }}>
            {analisando ? 'Analisando…' : 'Analisar com IA'}
          </Button>
        ) : null}
      </Stack>

      {erro ? (
        <Typography variant="body2" color="error">
          Análise indisponível: {erro}
        </Typography>
      ) : null}
      {!analise && !erro ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
      {analisando ? (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            A IA está analisando {analise?.campanhas} {analise?.campanhas === 1 ? 'campanha' : 'campanhas'}…
          </Typography>
          <LinearProgress sx={{ borderRadius: 1, mt: 0.5 }} />
        </Box>
      ) : null}

      {analise ? (
        <Box sx={{ opacity: analisando ? 0.6 : 1, transition: 'opacity 160ms ease' }}>
          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
            {analise.resumo}
          </Typography>

          {analise.sugestoes.length ? (
            <Stack spacing={1.25} sx={{ mt: 1.5 }} component="ol" style={{ listStyle: 'none', padding: 0, margin: '12px 0 0' }}>
              {analise.sugestoes.map((s, i) => (
                <Stack
                  key={`${s.campanha}-${i}`}
                  component="li"
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 0.75, sm: 1.5 }}
                  alignItems={{ xs: 'flex-start', sm: 'baseline' }}
                  sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}
                >
                  <Chip size="small" label={ACAO[s.acao].texto} color={ACAO[s.acao].cor} variant="outlined" sx={{ minWidth: 124 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2">
                      <strong>{s.campanha}</strong>{' '}
                      <Typography component="span" variant="caption" color="text.secondary">
                        {s.plataforma} · {PRIORIDADE[s.prioridade]}
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {s.texto}
                    </Typography>
                  </Box>
                  {(() => {
                    const a = acaoDaSugestao(s.acao)
                    // A sugestão traz o nome da plataforma ("Meta Ads"); o pedido precisa do código.
                    const plataforma = PLATAFORMAS.find((p) => p.nome === s.plataforma)?.id
                    if (!podeAplicar || !a || !s.campanha_id || !plataforma || !PLATAFORMAS_ALTERAVEIS[plataforma]) return null
                    return (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setAplicar({ pedido: { plataforma, nivel: 'campanha', objetoId: s.campanha_id!, nome: s.campanha, ...a }, motivo: s.texto })}
                        sx={{ textTransform: 'none', flexShrink: 0, alignSelf: { xs: 'flex-start', sm: 'center' } }}
                      >
                        Aplicar
                      </Button>
                    )
                  })()}
                </Stack>
              ))}
            </Stack>
          ) : null}

          <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              {podeAplicar
                ? 'Nada muda sozinho: só o que você confirmar em “Aplicar”.'
                : 'São sugestões para decidir: alterar as campanhas pelo sistema é de quem tem permissão.'}
            </Typography>
            {ia && analise.descartadas ? (
              <Typography variant="caption" color="text.secondary">
                {analise.descartadas} {analise.descartadas === 1 ? 'sugestão da IA foi descartada' : 'sugestões da IA foram descartadas'} na
                conferência dos números.
              </Typography>
            ) : null}
            {!ia && analise.motivoSemIa ? (
              <Typography variant="caption" color="text.secondary">
                Sem a análise da IA: {analise.motivoSemIa}.
              </Typography>
            ) : null}
          </Stack>
        </Box>
      ) : null}
      {aplicar ? (
        <AplicarAlteracao key={JSON.stringify(aplicar.pedido)} pedido={aplicar.pedido} origem="sugestao" motivo={aplicar.motivo} onFechar={() => setAplicar(null)} />
      ) : null}
    </Paper>
  )
}
